import {Component, Input} from '@angular/core';
import { KPStandard } from 'src/app/constants/standard/kp.standard';
import { SummarySettings } from 'src/app/constants/default/summary.constants';
import { FlightConfigurationDto } from 'src/app/dto/flightConfiguration/flightConfiguration.dto';
import { WeatherCode } from 'src/app/dto/summary/weather.code';
import { UnitDto } from 'src/app/dto/unit/unit.dto';
import { DirectionCode } from 'src/app/dto/weatherTable/direction.code';
import { DustDto } from 'src/app/dto/weatherTable/dust/dust.dto';
import { KpListDto } from 'src/app/dto/weatherTable/kp/kpList.dto';
import { FlightStatusCode } from 'src/app/dto/weatherTable/weather/flightStatus.code';
import { WeatherInfo } from 'src/app/dto/weatherTable/weather/weatherInfo.dto';
import { WeatherListDto } from 'src/app/dto/weatherTable/weather/weatherList.dto';
import { WeatherListItemDto } from 'src/app/dto/weatherTable/weatherListItem.dto';
import { DateService } from 'src/app/service/date/date.service';
import { LocalizationService } from 'src/app/service/localization/localization.service';

@Component({
    selector: 'summary',
    templateUrl: '../../view/summary/summary.html'
})
export class SummaryComponent {
    public kpInfoDto: KpListDto[] = [];
    public currentInfo!: WeatherListItemDto;
    public currentWindDirection!: string;
    public currentWindName!: string;
    public currentKp: number = 0;
    public todayInfo: WeatherListDto = new WeatherListDto();
    public weeklyInfo: WeatherListDto[] = [];
    public currentSatelliteCount!: number;
    public userUnit: UnitDto = new UnitDto();
    public selectedWeeklyIndex: number = 0;
    public dustInfo!: DustDto;
    public flightConfigurationDto!: FlightConfigurationDto;

    public isContainedGust: boolean = false;
    public isNotCurrent: boolean = false;

    public weatherTypeCode: typeof WeatherCode = WeatherCode;
    public directionCode: typeof DirectionCode = DirectionCode;
    public flightStatusCode: typeof FlightStatusCode = FlightStatusCode;
    public kpStandard: typeof KPStandard = KPStandard;

    public address!: string;

    constructor(public dateService: DateService, private localizationService: LocalizationService) { }

    public setAddress(address: string): void {
        this.address = address;
    }

    public setDustInfo(dustInfo: DustDto): void {
        this.dustInfo = Object.assign(new DustDto(), dustInfo);
    }

    public setFlightConfiguration(flightConfigurationDto: FlightConfigurationDto): void {
        this.flightConfigurationDto = Object.assign(new FlightConfigurationDto(), flightConfigurationDto);
    }

    public setWeatherList(weatherListDto: WeatherListDto[]): void {
        this.initCurrentInfo(weatherListDto);
        this.initTodayInfo(weatherListDto);
        this.initWeeklyInfo(weatherListDto);
    }

    private initCurrentInfo(weatherListDto: WeatherListDto[]): void {
        this.selectedWeeklyIndex = 0;
        this.currentInfo = Object.assign(new WeatherListItemDto(), weatherListDto[0].infos[0]);

        this.setCurrentWindInfo();
    }

    private setCurrentWindInfo(): void {
        this.currentWindDirection = this.localizationService.translate("summary.direction." + this.currentInfo.data.windDirection);
        this.currentWindName = this.localizationService.translate("summary.beaufort." + this.currentInfo.data.beaufortLevel);
    }

    private initTodayInfo(weatherListDto: WeatherListDto[]): void {
        this.todayInfo = Object.assign(new WeatherListDto(), weatherListDto[this.selectedWeeklyIndex]);
        this.todayInfo.infos = [];

        for (let i = 0; i < weatherListDto[this.selectedWeeklyIndex].infos.length; i++) {
            this.todayInfo.infos.push(Object.assign(new WeatherListItemDto(), weatherListDto[this.selectedWeeklyIndex].infos[i]));
        }

        this.checkTodayWindCount(weatherListDto);
    }

    private initWeeklyInfo(weatherListDto: WeatherListDto[]): void {
        this.weeklyInfo = [];

        for (let i = 0; i < weatherListDto.length; i++) {
            this.weeklyInfo.push(Object.assign(new WeatherListDto(), weatherListDto[i]));
            this.weeklyInfo[i].infos = [];

            let length = weatherListDto[i].infos.length;

            for (let j = 0; j < length; j++) {
                this.weeklyInfo[i].infos.push(Object.assign(new WeatherListItemDto(), weatherListDto[i].infos[j]));
            }
        }
    }

    private checkTodayWindCount(weatherListDto: WeatherListDto[]): void {
        let limit: number = SummarySettings.TODAY_WIND_COUNT;
        let nextIndex: number = this.selectedWeeklyIndex + 1;

        if (this.todayInfo.infos.length < limit) {
            for (let i = 0; this.todayInfo.infos.length < limit; i++) {
                if (weatherListDto[nextIndex].infos.length <= i ) {
                    i = 0;
                    nextIndex++;
                }

                this.todayInfo.infos.push(Object.assign(new WeatherListItemDto(), weatherListDto[nextIndex].infos[i]));
            }
        }

        this.todayInfo.setMaxWindSpeed();
    }

    public setUserUnit(unitDto: UnitDto): void {
        this.userUnit = Object.assign(new UnitDto(), unitDto);
    }

    public setKpList(kpListDto: KpListDto[]): void {
        this.initKpListInfo(kpListDto);
    }

    private initKpListInfo(kpListDto: KpListDto[]): void {
        this.kpInfoDto = [];

        for (let i = 0; i < kpListDto.length; i++) {
            let tempKp: KpListDto = Object.assign(new KpListDto(), kpListDto[i])
            this.kpInfoDto.push(tempKp);
        }

        this.currentKp = this.kpInfoDto[0].infos[0].kp;
    }

    public setCurrentSatelliteCount(currentSatelliteCount: number): void {
        this.currentSatelliteCount = currentSatelliteCount;
    }

    public getCurrentInfo(): WeatherListItemDto {
        if (this.currentInfo) {
            return this.currentInfo;
        }

        return this.getDefaultCurrentInfo();
    }

    private getDefaultCurrentInfo(): WeatherListItemDto {
        let defaultInfo: WeatherListItemDto = new WeatherListItemDto();

        defaultInfo.data = new WeatherInfo();
        defaultInfo.data.windSpeed = 0;
        defaultInfo.data.windGust = 0;
        defaultInfo.data.temperature = 0;

        return defaultInfo;
    }

    public setContainedGust(isContainedGust: boolean): void {
        this.isContainedGust = isContainedGust;
    }

    private getCurrentStatusCode(): FlightStatusCode {
        if (this.selectedWeeklyIndex == 0) {
            return this.currentInfo.isPossibleFlightWithSatellite(this.flightConfigurationDto, this.currentSatelliteCount, this.isContainedGust);
        } else {
            return this.currentInfo.isPossibleFlight(this.flightConfigurationDto, this.isContainedGust);
        }
    }

    public getBackgroundStyleName(): string {
        if (this.currentInfo && this.currentSatelliteCount) {
        //if (this.currentInfo) {
            if (this.currentKp >= KPStandard.DANGEROUS_KP) {
                return "type-2";
            }

            let resultStatus: FlightStatusCode = this.getCurrentStatusCode();

            switch (resultStatus) {
                case FlightStatusCode.GOOD :
                    return "type-1";
                case FlightStatusCode.DANGER :
                    return "type-2";
                case FlightStatusCode.CARE :
                    return "type-3";
            }
        }

        return "type-1";
    }

    public getBackgroundWeatherStyleName(): string {
        if (this.currentInfo) {
            if (this.currentInfo.data.ptype == 0) {
                return "wrap-sunny";
            } else if (this.currentInfo.data.ptype == 1) {
                return "wrap-rainy";
            } else if (this.currentInfo.data.ptype == 5 || this.currentInfo.data.ptype == 6) {
                return "wrap-snowy";
            }
        }

        return "wrap-sunny";
    }

    public getWeatherStyleName(): string {
        if (this.currentInfo) {
            if (this.currentInfo.data.ptype == 0) {
                return "type-2";
            } else if (this.currentInfo.data.ptype == 1) {
                return "type-1";
            } else if (this.currentInfo.data.ptype == 5 || this.currentInfo.data.ptype == 6) {
                return "type-4";
            }
        }

        return "type-1";
    }

    public getDailyWeatherStyleName(index: number): string {
        switch (this.weeklyInfo[index].getWeatherCode()) {
            case WeatherCode.SUNNY:
                return "type-6";
            case WeatherCode.RAINY:
                return "type-4";
            case WeatherCode.CLOUDY:
                return "type-1";
            case WeatherCode.WINDY:
                return "type-7";
            case WeatherCode.SNOWY:
                return "type-5";
        }
    }

    public getCurrentKpInfo(): number {
        if (0 < this.kpInfoDto.length) {
            return this.kpInfoDto[0].infos[0].kp;
        }

        return 0;
    }

    public isPossibleFlight(): FlightStatusCode {
        let statusCode: FlightStatusCode = FlightStatusCode.GOOD;

        if (this.currentInfo && this.currentSatelliteCount) {
        //if (this.currentInfo) {
            if (this.selectedWeeklyIndex == 0) {
                statusCode = this.currentInfo.isPossibleFlightWithSatellite(this.flightConfigurationDto, this.currentSatelliteCount, this.isContainedGust);
            } else {
                statusCode = this.currentInfo.isPossibleFlight(this.flightConfigurationDto, this.isContainedGust);
            }
        }

        if (this.currentKp >= KPStandard.DANGEROUS_KP) {
            return FlightStatusCode.DANGER;
        }

        return statusCode;
    }

    public changeWeeklyInfo(weeklyIndex: number): void {
        this.selectedWeeklyIndex = weeklyIndex;
        this.changeCurrentInfo();
    }

    private changeCurrentInfo(): void {
        this.setCurrentInfoFromWeeklyInfo();
        this.setCurrentKpFromKpInfo();
        this.initTodayInfo(this.weeklyInfo);
    }

    private setCurrentInfoFromWeeklyInfo(): void {
        if (this.selectedWeeklyIndex == 0) {
            this.initCurrentInfo(this.weeklyInfo);
        } else {
            let selectedDayInfo: WeatherListDto = this.weeklyInfo[this.selectedWeeklyIndex];

            for (let i = 0; i < selectedDayInfo.infos.length; i++) {
                if (selectedDayInfo.sunrise <= selectedDayInfo.infos[i].timeTag) {
                    this.currentInfo = Object.assign(new WeatherListItemDto(), selectedDayInfo.infos[i]);
                    this.setCurrentWindInfo();
                    break;
                }
            }
        }
    }

    private setCurrentKpFromKpInfo(): void {
        if (this.selectedWeeklyIndex == 0) {
            this.currentKp = this.kpInfoDto[0].infos[0].kp;
        } else {
            this.currentKp = 0;

            if (this.selectedWeeklyIndex <= this.kpInfoDto.length) {
                for (let i = 0; i < this.kpInfoDto[this.selectedWeeklyIndex]?.infos?.length; i++) {
                    let kpInfo = this.kpInfoDto[this.selectedWeeklyIndex].infos[i];

                    if (this.weeklyInfo[this.selectedWeeklyIndex].sunrise <= kpInfo.timeTag) {
                        this.currentKp = kpInfo.kp;
                        break;
                    }
                }
            }
        }
    }

    public getDailyWeatherStatusCode(index: number): FlightStatusCode {
        let statusCode: FlightStatusCode;

        if (index == 0) {
            statusCode = this.weeklyInfo[index].isPossibleFlightWithSatellite(this.flightConfigurationDto, this.currentSatelliteCount, this.isContainedGust);
        } else {
            statusCode = this.weeklyInfo[index].isPossibleFlight(this.flightConfigurationDto, this.isContainedGust);
        }

        if (this.kpInfoDto.length >= index) {
            for (let i = 0; i < this.kpInfoDto[index]?.infos?.length; i++) {
                let kpInfo = this.kpInfoDto[index].infos[i];

                if (this.weeklyInfo[index].sunrise <= kpInfo.timeTag) {
                    if (kpInfo.kp >= KPStandard.DANGEROUS_KP) {
                        return FlightStatusCode.DANGER;
                    }
                    break;
                }

            }
        }

        return statusCode;
    }
}
